import { makeAutoObservable, runInAction } from 'mobx';
import i18n from 'i18n';
import { BACKEND_MODULE_TYPES, MAX_UPLOADED_FILES_SIZE, TOPIC_MESSAGES_SIZE } from 'components/common/constants';
import { getUnreadTopicsAmount } from 'components/Messages/utils';
import { setTimerDurationExpiresAt, updateConfirmationActionType } from 'services/authUtils';
import {
  confirmMessageCreation,
  confirmTopicCreation,
  createMessage,
  createTopic,
  downloadMessageDocument,
  forceChangeConfirmationTypeRequest,
  getMessageList,
  getTopicMessages,
  markTopicAsReadRequest,
  resendSecurityCode,
  uploadMessageDocument
} from 'services/requestAgent';
import { convertDateToMessagesFilter } from '../services/utils';

class MessagesStore {
  isInitialized = false;
  isLoading = false;
  isTopicMessagesLoading = false;
  isFileUploading = false;
  isNewTopicCreated = false;
  isNewMessageCreated = false;
  isShowingInternalActionConfirmation = false;
  isShowSwitchConfirmationMethodScreen = false;
  error = null;
  temporaryError = null;
  topics = [];
  totalElements = null;
  totalPages = null;
  hasAnyTopics = false; // Флаг для отслеживания наличия топиков вообще
  filteredTopics = [];
  unreadTopics = null;
  currentTopicIndex = -1;
  currentTopicMessages = [];
  securityCode = '';
  confirmationId = null;

  // Pagination for messages within a topic
  messagesPagination = {
    page: 0,
    size: 20,
    totalPages: null,
    totalElements: null,
    isFirstPage: false
  };
  isLoadingOlderMessages = false;
  uploadedFiles = [];
  newTopic = {
    subject: '',
    message_body: ''
  };
  newMessage = {
    body: ''
  };

  filters = {
    statuses: 'ALL',
    search_text: '',
    from: null,
    to: null
  };

  pagination = {
    size: 6,
    page: 0
  };

  topicsSort = {
    sort_column: 'updated_at',
    sort_direction: 'DESC'
  };

  constructor() {
    makeAutoObservable(this);
  }

  async initializeData(account_number) {
    try {
      runInAction(() => {
        this.isLoading = true;
        // Сбрасываем фильтры при инициализации
        this.clearFilters();
      });

      await this.getTopics(account_number);

      const unreadTopics = getUnreadTopicsAmount(this.topics);

      runInAction(() => {
        this.unreadTopics = unreadTopics;
        this.isInitialized = true;
        // При инициализации устанавливаем флаг наличия топиков
        this.hasAnyTopics = this.totalElements > 0;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  resetForms() {
    this.isLoading = false;
    this.isShowingInternalActionConfirmation = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.error = null;
    this.securityCode = '';
    this.confirmationId = null;
    this.uploadedFiles = [];
    this.newTopic = {
      subject: '',
      message_body: ''
    };
    this.newMessage = {
      body: ''
    };
  }

  clearError() {
    this.error = null;
  }

  setTemporaryError(error) {
    this.temporaryError = error;
    if (error) {
      // Clear temporary error after 5 seconds
      setTimeout(() => {
        runInAction(() => {
          if (this.temporaryError?.code === error?.code) {
            this.temporaryError = null;
          }
        });
      }, 5000);
    }
  }

  clearUploadedFiles() {
    this.uploadedFiles = [];
  }

  setError(error) {
    this.error = error;
  }

  setIsNewTopicCreated(status) {
    this.isNewTopicCreated = status;
  }

  setIsNewMessageCreated(status) {
    this.isNewMessageCreated = status;
  }

  setIsShowingInternalActionConfirmation(status) {
    this.isShowingInternalActionConfirmation = status;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }

  setCurrentTopicIndex(index) {
    this.currentTopicIndex = index;
  }

  getCurrentTopicIndexById(topicId) {
    return this.topics.findIndex((topic) => topic.id === topicId);
  }

  setNewTopicData = (subject, message) => {
    this.newTopic.subject = subject;
    this.newTopic.message_body = message;
  };

  setNewMessageData = (message) => {
    this.newMessage.body = message;
  };

  setIsFileUploading(status) {
    this.isFileUploading = status;
    this.error = null;
  }

  removeAttachment(fileId) {
    this.uploadedFiles = this.uploadedFiles.filter((file) => file?.id !== fileId);
  }

  uploadAttachments(customerNumber) {
    return async ({ target: { files } }) => {
      if (!Object.keys(files).length) {
        return null;
      }
      this.setIsFileUploading(true);
      try {
        const selectedFiles = [...files];
        const Data = new FormData();
        selectedFiles.forEach((file) => {
          if (file.size > MAX_UPLOADED_FILES_SIZE) {
            throw { code: 'REQUEST_HAS_BEEN_TERMINATED' };
          } else {
            Data.append('file', file, file?.name);
          }
        });
        const documentArray = await uploadMessageDocument(customerNumber, Data);

        runInAction(() => {
          this.isFileUploading = false;
          this.uploadedFiles = [
            ...this.uploadedFiles,
            ...documentArray.map((file, index) => ({
              name: file?.name,
              id: file?.id,
              // Keep reference to original file for preview
              originalFile: selectedFiles[index],
              type: selectedFiles[index]?.type,
              size: selectedFiles[index]?.size
            }))
          ];
        });
      } catch (err) {
        runInAction(() => {
          this.isFileUploading = false;
          this.error = { type: 'attachDoc', ...err };
        });
      }
    };
  }

  async downloadAttachment(customerNumber, fileId) {
    try {
      runInAction(() => {
        this.isFileUploading = true;
      });
      await downloadMessageDocument(customerNumber, fileId, false);
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isFileUploading = false;
      });
    }
  }

  async generateNewTopicSecurityCode(customerNumber) {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await createTopic(customerNumber, {
        ...this.newTopic,
        documents: this.uploadedFiles.map((file) => file.id)
      });

      updateConfirmationActionType(confirmationType);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.isShowingInternalActionConfirmation = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async generateNewMessageSecurityCode(customerNumber, topicId) {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await createMessage(customerNumber, topicId, {
        ...this.newMessage,
        documents: this.uploadedFiles.map((file) => file.id)
      });

      updateConfirmationActionType(confirmationType);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.isShowingInternalActionConfirmation = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async resendMessageSecurityCode() {
    try {
      runInAction(() => {
        this.isLoading = true;
      });
      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await resendSecurityCode(BACKEND_MODULE_TYPES.MESSENGER, this.confirmationId);

      updateConfirmationActionType(confirmationType);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.isShowingInternalActionConfirmation = true;
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async confirmNewTopicCreation(customerNumber, securityCode) {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const newTopic = await confirmTopicCreation(customerNumber, {
        code: securityCode,
        confirmation_id: this.confirmationId
      });

      runInAction(() => {
        this.uploadedFiles = [];
        this.topics = [newTopic, ...this.topics];
        this.hasAnyTopics = true; // Обновляем флаг наличия топиков
        this.totalElements = (this.totalElements || 0) + 1; // Увеличиваем счетчик элементов
        this.isNewTopicCreated = true;
        this.resetForms();
      });

      // Загружаем все топики для синхронизации с сервером
      await this.getTopics(customerNumber);
    } catch (err) {
      runInAction(() => {
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.error = err;
          this.isShowingInternalActionConfirmation = false;
          // Reset to message form state
          this.confirmationId = null;
          this.securityCode = '';
          this.error = {
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage
              ? i18n.getMessage('error.confirmationRejected')
              : 'Push notification was rejected. Please try again.'
          };
          // Show toast notification about rejection
          this.setTemporaryError({
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage
              ? i18n.getMessage('error.confirmationRejected')
              : 'Push notification was rejected. Please try again.'
          });
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
          this.error = err;
          this.securityCode = '';
        }
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async getCurrentTopicMessagesFilesUrls(customerNumber) {
    try {
      for (const message of this.currentTopicMessages) {
        if (message.documents?.length) {
          for (const document of message.documents) {
            if (!document.url) {
              const blobUrl = await downloadMessageDocument(customerNumber, document.id, true);
              runInAction(() => {
                document.url = blobUrl;
              });
            }
          }
        }
      }
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    }
  }

  async markTopicAsRead(accountNumber, topicId, topicIndex) {
    try {
      runInAction(() => {
        this.isTopicMessagesLoading = true;
      });

      const updatedTopic = await markTopicAsReadRequest(accountNumber, topicId);

      runInAction(() => {
        this.unreadTopics--;
        this.topics[topicIndex] = updatedTopic;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isTopicMessagesLoading = false;
      });
    }
  }

  async getCurrentTopicMessages(customerNumber, topicId, loadDirection = 'latest') {
    try {
      runInAction(() => {
        if (loadDirection === 'latest') {
          this.isTopicMessagesLoading = true;
          this.currentTopicMessages = []; // Reset when loading latest page
        } else {
          this.isLoadingOlderMessages = true;
        }
      });

      let queryParams;

      if (loadDirection === 'latest') {
        // Load the last page with newest messages
        queryParams = {
          size: this.messagesPagination.size,
          page: 0,
          sort_direction: 'DESC'
        };
      } else {
        // Load previous page with older messages
        queryParams = {
          size: this.messagesPagination.size,
          page: this.messagesPagination.page + 1,
          sort_direction: 'DESC'
        };
      }

      const response = await getTopicMessages(customerNumber, topicId, queryParams);

      runInAction(() => {
        if (loadDirection === 'latest') {
          // Reverse messages to show from old to new (bottom to top)
          this.currentTopicMessages = response.content ? [...response.content].reverse() : [];
          this.messagesPagination.totalPages = response.totalPages || 1;
          this.messagesPagination.totalElements = response.totalElements || 0;
          this.messagesPagination.page = 0;
          this.messagesPagination.isFirstPage = response.totalPages <= 1;
        } else {
          // Prepend older messages at the beginning of the list
          const olderMessages = response.content ? [...response.content].reverse() : [];
          this.currentTopicMessages = [...olderMessages, ...this.currentTopicMessages];
          this.messagesPagination.page++;
          // Fix: use response.totalPages instead of this.messagesPagination.totalPages
          this.messagesPagination.isFirstPage = this.messagesPagination.page >= response.totalPages - 1;
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        if (loadDirection === 'latest') {
          this.isTopicMessagesLoading = false;
        } else {
          this.isLoadingOlderMessages = false;
        }
      });
    }
  }

  async loadPreviousMessages(customerNumber, topicId) {
    if (this.messagesPagination.isFirstPage || this.isLoadingOlderMessages) {
      return false;
    }
    await this.getCurrentTopicMessages(customerNumber, topicId, 'previous');
    return true;
  }

  resetMessagesPagination() {
    this.messagesPagination = {
      page: 0,
      size: 20,
      totalPages: null,
      totalElements: null,
      isFirstPage: false
    };
  }

  async confirmNewMessageCreation(customerNumber, topicId, securityCode) {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const newMessage = await confirmMessageCreation(customerNumber, topicId, {
        code: securityCode,
        confirmation_id: this.confirmationId
      });

      runInAction(() => {
        this.uploadedFiles = [];
        this.currentTopicMessages = [...this.currentTopicMessages, newMessage];
        this.topics[this.currentTopicIndex].last_message = newMessage;
        this.topics.sort((a, b) => new Date(b?.last_message?.create_at) - new Date(a?.last_message?.create_at));
        this.isNewMessageCreated = true;
        this.setCurrentTopicIndex(0);
        this.resetForms();
      });
    } catch (err) {
      runInAction(() => {
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.error = err;
          this.isShowingInternalActionConfirmation = false;
          // Reset to message form state
          this.confirmationId = null;
          this.securityCode = '';
          this.error = {
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage
              ? i18n.getMessage('error.confirmationRejected')
              : 'Push notification was rejected. Please try again.'
          };
          // Show toast notification about rejection
          this.setTemporaryError({
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage
              ? i18n.getMessage('error.confirmationRejected')
              : 'Push notification was rejected. Please try again.'
          });
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
          this.error = err;
          this.securityCode = '';
        }
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await forceChangeConfirmationTypeRequest(
        this.confirmationId,
        confirmationType,
        BACKEND_MODULE_TYPES.MESSENGER
      );

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };

  async getTopics(customerNumber) {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const query = {
        ...this.filters,
        ...this.pagination,
        ...this.topicsSort,
        from: convertDateToMessagesFilter(this.filters.from),
        to: convertDateToMessagesFilter(this.filters.to)
      };

      // Handle statuses - send as array or empty if All is selected
      if (query.statuses && query.statuses !== 'ALL') {
        query.statuses = Array.isArray(query.statuses) ? query.statuses : [query.statuses];
      } else {
        delete query.statuses; // Remove if null or 'ALL'
      }

      const { content: data, total_elements, total_pages, number, size } = await getMessageList(customerNumber, query);

      runInAction(() => {
        this.totalElements = total_elements;
        this.totalPages = total_pages;
        this.pagination.size = size;
        this.pagination.page = number;
        this.topics = data;
        // Если есть хоть один элемент или фильтры не пустые, значит в системе есть топики
        if (total_elements > 0 || !this.hasActiveFilters()) {
          this.hasAnyTopics = total_elements > 0;
        }
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isLoading = false;
      });
      throw err;
    }
  }

  setFilter(fieldName, value) {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  }

  clearFilters() {
    this.filters = {
      statuses: 'ALL',
      search_text: '',
      from: null,
      to: null
    };

    this.pagination = {
      size: 6,
      page: 0
    };

    this.topicsSort = {
      sort_column: 'updated_at',
      sort_direction: 'DESC'
    };
  }

  setPage(page) {
    this.pagination.page = page;
  }

  setPageSize(size) {
    this.pagination.size = size;
  }

  hasActiveFilters() {
    return !!(this.filters.search_text || this.filters.statuses !== null || this.filters.from || this.filters.to);
  }

  clearMessagesStore() {
    this.isInitialized = false;
    this.isLoading = false;
    this.isTopicMessagesLoading = false;
    this.isFileUploading = false;
    this.isNewTopicCreated = false;
    this.isNewMessageCreated = false;
    this.isShowingInternalActionConfirmation = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.error = null;
    this.topics = [];
    this.totalElements = null;
    this.totalPages = null;
    this.hasAnyTopics = false;
    this.filteredTopics = [];
    this.unreadTopics = null;
    this.currentTopicIndex = -1;
    this.currentTopicMessages = [];
    this.securityCode = '';
    this.confirmationId = null;
    this.uploadedFiles = [];
    this.newTopic = {
      subject: '',
      message_body: ''
    };
    this.newMessage = {
      body: ''
    };
    this.filters = {
      statuses: 'ALL',
      search_text: '',
      from: null,
      to: null
    };
    this.pagination = {
      size: 6,
      page: 0
    };
    this.topicsSort = {
      sort_column: 'updated_at',
      sort_direction: 'DESC'
    };
  }
}

export default new MessagesStore();
